<script>
import Title from "@/components/admin/common/TitleArea.vue"
import StatusBadge from "@/components/fulfillment/StatusBadge.vue"
import PartnerSkuRequestNew from "@/components/admin/fulfillment/modal/PartnerSkuRequestNew.vue"
import PageGuideDialog from "@/components/admin/fulfillment/guide/PageGuideDialog.vue"
import PartnerFulfillmentGuide from "@/components/admin/fulfillment/guide/PartnerFulfillmentGuide.vue"

const emptyDashboard = () => ({
  products: {
    total: 0,
    active: 0,
    inactive: 0,
    brandCount: 0,
    categoryCount: 0,
  },
  requests: {
    total: 0,
    requested: 0,
    rejected: 0,
    newRequested: 0,
    linkRequested: 0,
  },
  brandDistribution: [],
  categoryDistribution: [],
  recentProducts: [],
  actionRequests: [],
})

export default {
  name: 'PartnerFulfillmentHome',
  components: {
    Title,
    StatusBadge,
    PartnerSkuRequestNew,
    PageGuideDialog,
    PartnerFulfillmentGuide,
  },
  data() {
    return {
      description: {
        title: '내 상품 현황'
      },
      dashboard: emptyDashboard(),
      loading: false,
      guideDialog: false,
      showNewModal: false,
    }
  },
  watch: {
    '$route.fullPath': {
      immediate: true,
      handler() {
        this.applyRouteContext()
      }
    }
  },
  methods: {
    applyRouteContext() {
      const routeTitle = String(this.$route?.meta?.title || '').replace(/^Aone 관리자 \|\s*/, '')
      if (routeTitle) {
        this.description.title = routeTitle
      }
    },
    async loadDashboard() {
      this.loading = true
      try {
        const res = await this.$http({
          method: 'GET',
          url: '/api/fulfillment/partner/sku-products/dashboard',
          name: '[ Fulfillment / Partner 상품 현황 ]'
        })
        this.dashboard = {
          ...emptyDashboard(),
          ...res.data?.data,
        }
      } catch (_) {
        this.dashboard = emptyDashboard()
      } finally {
        this.loading = false
      }
    },
    getRequestTypeLabel(row) {
      return row.request_type === 'LINK_REQUEST' ? '연결요청' : '신규등록요청'
    },
    getProductStatus(row) {
      return Number(row.partner_sku_is_active) === 1 && Number(row.master_sku_is_active) === 1
        ? '사용 가능'
        : '사용 중지'
    },
    getProductStatusColor(row) {
      return this.getProductStatus(row) === '사용 가능' ? 'positive' : 'grey-7'
    },
    distributionWidth(count, rows) {
      const max = Math.max(...rows.map(row => Number(row.product_count || 0)), 1)
      return `${Math.max((Number(count || 0) / max) * 100, 8)}%`
    },
    formatDateTime(value) {
      return value ? this.$formatDate(value, 'YYYY-MM-DD HH:mm:ss') : '-'
    },
    goToNewRequest() {
      this.showNewModal = true
    },
    closeNewModal() {
      this.showNewModal = false
    },
    async onCreated() {
      this.closeNewModal()
      await this.loadDashboard()
    },
    goToProductList() {
      this.$router.push('/admin/fulfillment/sku-requests')
    },
    goToProductDetail(row) {
      this.$router.push({
        path: '/admin/fulfillment/my-products/detail',
        query: { partner_sku_code: row.partner_sku_code }
      })
    },
  },
  async mounted() {
    this.applyRouteContext()
    await this.loadDashboard()
  }
}
</script>

<template>
  <q-page class="q-pa-lg">
    <Title :desc="description">
      <template #title-action>
        <q-btn flat round dense size="sm" color="primary" icon="help_outline" aria-label="내 상품 현황 사용 가이드" @click="guideDialog = true">
          <q-tooltip>내 상품 현황 사용 가이드</q-tooltip>
        </q-btn>
      </template>
      <div class="row q-gutter-sm justify-end header-actions">
        <q-btn flat color="primary" label="내 상품목록 관리" @click="goToProductList" />
        <q-btn color="primary" label="상품 요청 등록" @click="goToNewRequest" />
      </div>
    </Title>

    <div class="text-caption text-grey-7 q-mt-sm q-mb-lg page-intro">
      현재 취급 중인 상품 구성과 등록·연결 요청을 분리하여 확인합니다.
    </div>

    <div class="section-heading q-mb-sm">
      <div class="text-subtitle1 text-weight-bold">취급 상품 현황</div>
      <div class="text-caption text-grey-7">기준 상품과 연결된 내 SKU만 집계합니다.</div>
    </div>

    <div class="metric-grid q-mb-md">
      <div class="comp-box q-pa-md metric-card">
        <div class="text-caption text-grey-7">전체 취급 상품</div>
        <div class="metric-value">{{ dashboard.products.total }}</div>
        <div class="text-caption text-grey">연결이 완료된 내 SKU</div>
      </div>
      <div class="comp-box q-pa-md metric-card">
        <div class="text-caption text-grey-7">사용 가능</div>
        <div class="metric-value text-positive">{{ dashboard.products.active }}</div>
        <div class="text-caption text-grey">현재 운영 가능한 상품</div>
      </div>
      <div class="comp-box q-pa-md metric-card">
        <div class="text-caption text-grey-7">사용 중지</div>
        <div class="metric-value text-grey-7">{{ dashboard.products.inactive }}</div>
        <div class="text-caption text-grey">연결 또는 기준 상품 비활성</div>
      </div>
      <div class="comp-box q-pa-md metric-card">
        <div class="text-caption text-grey-7">브랜드</div>
        <div class="metric-value">{{ dashboard.products.brandCount }}</div>
        <div class="text-caption text-grey">취급 중인 브랜드 수</div>
      </div>
      <div class="comp-box q-pa-md metric-card">
        <div class="text-caption text-grey-7">카테고리</div>
        <div class="metric-value">{{ dashboard.products.categoryCount }}</div>
        <div class="text-caption text-grey">취급 중인 카테고리 수</div>
      </div>
    </div>

    <div class="row q-col-gutter-md q-mb-md items-stretch">
      <div class="col-12 col-md-6">
        <div class="comp-box q-pa-md full-height">
          <div class="text-subtitle1 text-weight-bold">브랜드별 취급 상품</div>
          <div class="text-caption text-grey-7 q-mb-md">상품 수가 많은 브랜드 상위 5개입니다.</div>
          <div v-if="dashboard.brandDistribution.length" class="q-gutter-md">
            <div v-for="row in dashboard.brandDistribution" :key="row.brand_id">
              <div class="row justify-between text-caption q-mb-xs">
                <span>{{ row.name }}</span>
                <span class="text-weight-bold">{{ row.product_count }}</span>
              </div>
              <div class="distribution-track">
                <div class="distribution-bar" :style="{ width: distributionWidth(row.product_count, dashboard.brandDistribution) }" />
              </div>
            </div>
          </div>
          <div v-else class="empty-state">취급 중인 브랜드가 없습니다.</div>
        </div>
      </div>
      <div class="col-12 col-md-6">
        <div class="comp-box q-pa-md full-height">
          <div class="text-subtitle1 text-weight-bold">카테고리별 취급 상품</div>
          <div class="text-caption text-grey-7 q-mb-md">상품 수가 많은 카테고리 상위 5개입니다.</div>
          <div v-if="dashboard.categoryDistribution.length" class="q-gutter-md">
            <div v-for="row in dashboard.categoryDistribution" :key="row.category_id">
              <div class="row justify-between text-caption q-mb-xs">
                <span>{{ row.name }}</span>
                <span class="text-weight-bold">{{ row.product_count }}</span>
              </div>
              <div class="distribution-track">
                <div class="distribution-bar distribution-bar--category" :style="{ width: distributionWidth(row.product_count, dashboard.categoryDistribution) }" />
              </div>
            </div>
          </div>
          <div v-else class="empty-state">취급 중인 카테고리가 없습니다.</div>
        </div>
      </div>
    </div>

    <div class="row q-col-gutter-md items-stretch">
      <div class="col-12 col-lg-7">
        <div class="comp-box q-pa-md full-height">
          <div class="row items-center justify-between q-mb-md">
            <div>
              <div class="text-subtitle1 text-weight-bold">최근 취급 상품</div>
              <div class="text-caption text-grey-7">최근 변경된 연결 상품을 확인합니다.</div>
            </div>
            <q-btn flat dense color="primary" label="전체보기" icon-right="arrow_forward" @click="goToProductList" />
          </div>
          <div v-if="dashboard.recentProducts.length" class="product-list">
            <button v-for="row in dashboard.recentProducts" :key="row.partner_sku_code" type="button" class="product-row" @click="goToProductDetail(row)">
              <div class="product-row__main">
                <div class="text-weight-medium">{{ row.partner_display_name || row.display_name || row.partner_sku_code }}</div>
                <div class="text-caption text-grey-7">{{ row.partner_sku_code }} · {{ row.barcode || '바코드 없음' }} · {{ row.brand_name || '-' }} / {{ row.category_name || '-' }}</div>
              </div>
              <q-badge :color="getProductStatusColor(row)" :label="getProductStatus(row)" />
              <q-icon name="chevron_right" color="grey-6" size="20px" />
            </button>
          </div>
          <div v-else class="empty-state">아직 연결된 취급 상품이 없습니다.</div>
        </div>
      </div>

      <div class="col-12 col-lg-5">
        <div class="comp-box q-pa-md full-height">
          <div class="row items-center justify-between q-mb-md">
            <div>
              <div class="text-subtitle1 text-weight-bold">등록·연결 요청 현황</div>
              <div class="text-caption text-grey-7">처리가 필요한 요청만 보조적으로 확인합니다.</div>
            </div>
            <q-btn flat dense color="primary" icon="add" aria-label="상품 요청 등록" @click="goToNewRequest">
              <q-tooltip>상품 요청 등록</q-tooltip>
            </q-btn>
          </div>
          <div class="request-grid q-mb-md">
            <div class="request-tile">
              <span>검토 중</span>
              <b>{{ dashboard.requests.requested }}</b>
            </div>
            <div class="request-tile request-tile--alert">
              <span>반려</span>
              <b>{{ dashboard.requests.rejected }}</b>
            </div>
            <div class="request-tile">
              <span>신규등록요청</span>
              <b>{{ dashboard.requests.newRequested }}</b>
            </div>
            <div class="request-tile">
              <span>연결요청</span>
              <b>{{ dashboard.requests.linkRequested }}</b>
            </div>
          </div>
          <div v-if="dashboard.actionRequests.length" class="q-gutter-sm">
            <div v-for="row in dashboard.actionRequests" :key="row.request_id" class="request-row">
              <div>
                <div class="text-weight-medium">{{ row.partner_sku_code }}</div>
                <div class="text-caption text-grey-7">{{ row.barcode || '바코드 없음' }}</div>
                <div class="text-caption text-grey-7">{{ getRequestTypeLabel(row) }} · {{ formatDateTime(row.updated_at || row.created_at) }}</div>
              </div>
              <StatusBadge :status="row.status" />
            </div>
          </div>
          <div v-else class="empty-state">현재 확인할 요청이 없습니다.</div>
        </div>
      </div>
    </div>

    <q-inner-loading v-if="loading" :showing="true">
      <q-spinner color="primary" size="42px" />
    </q-inner-loading>

    <PageGuideDialog
      v-model="guideDialog"
      title="내 상품 현황 사용 가이드"
      description="취급 상품 구성과 등록·연결 요청을 확인하는 방법입니다."
    >
      <PartnerFulfillmentGuide guide-key="home" />
    </PageGuideDialog>
  </q-page>

  <teleport to="body">
    <q-dialog v-model="showNewModal" maximized persistent>
      <PartnerSkuRequestNew
        :in-modal="true"
        @created="onCreated"
        @close="closeNewModal"
      />
    </q-dialog>
  </teleport>
</template>

<style scoped lang="scss">
.page-intro {
  max-width: 720px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(180px, 1fr));
  gap: 16px;
  overflow-x: auto;
  padding-bottom: 2px;
}

.metric-card {
  min-height: 132px;
}

.metric-value {
  margin: 8px 0;
  color: #183b56;
  font-size: 30px;
  font-weight: 700;
  line-height: 1;
}

.full-height {
  height: 100%;
}

.distribution-track {
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #edf2f1;
}

.distribution-bar {
  height: 100%;
  border-radius: inherit;
  background: #187b70;
}

.distribution-bar--category {
  background: #df8f32;
}

.product-list,
.request-row {
  border-top: 1px solid #edf0f2;
}

.product-row {
  display: grid;
  width: 100%;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 12px;
  align-items: center;
  padding: 12px 0;
  border: 0;
  border-bottom: 1px solid #edf0f2;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.product-row:hover {
  background: #f7faf9;
}

.product-row__main {
  min-width: 0;
}

.request-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.request-tile {
  display: flex;
  min-height: 68px;
  flex-direction: column;
  justify-content: space-between;
  padding: 10px;
  border: 1px solid #e3e9e7;
  border-radius: 8px;
  background: #f8fbfa;
  color: #607171;
  font-size: 12px;
}

.request-tile b {
  color: #183b56;
  font-size: 18px;
}

.request-tile--alert {
  border-color: #f2d4ce;
  background: #fff8f6;
}

.request-tile--alert b {
  color: #c54b3c;
}

.request-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
}

.empty-state {
  padding: 28px 12px;
  color: #899593;
  font-size: 13px;
  text-align: center;
}

@media (max-width: 599px) {
  .header-actions {
    margin-top: 8px;
  }

  .request-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
